import React from 'react'
import { Card, Row, Col, Spinner, ProgressBar } from 'react-bootstrap'
import { BillingAnalytics } from '../types'
import * as S from '../styles'

interface BillingAnalyticsCardProps {
  data?: BillingAnalytics
  loading: boolean
}

// total = 0 (empresa sem dados) dava NaN% / R$ NaN na tela
const percentOf = (part: number, total: number) =>
  total > 0 ? (part / total) * 100 : 0

export const BillingAnalyticsCard: React.FC<BillingAnalyticsCardProps> = ({
  data,
  loading
}) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value)
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value)
  }

  if (loading || !data) {
    return (
      <Card className="h-100">
        <Card.Header>
          <Card.Title as="h5">
            <i className="feather icon-credit-card text-warning mr-2" />
            Análise de Cobranças
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <S.LoadingSpinner>
            <Spinner animation="border" />
          </S.LoadingSpinner>
        </Card.Body>
      </Card>
    )
  }

  const paidPercentage = percentOf(data.paidAmount, data.totalValue)
  const pendingPercentage = percentOf(data.pendingAmount, data.totalValue)
  const overduePercentage = percentOf(data.overdueAmount, data.totalValue)
  const blockchainPercentage = percentOf(
    data.blockchainRegisteredBillings,
    data.totalBillings
  )

  return (
    <Card className="h-100">
      <Card.Header>
        <Card.Title as="h5">
          <i className="feather icon-credit-card text-warning mr-2" />
          Análise de Cobranças
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <Row className="mb-4">
          <Col sm={6}>
            <div className="text-center">
              <h3 className="text-primary mb-1">
                {formatNumber(data.totalBillings)}
              </h3>
              <p className="text-muted mb-0">Total de Cobranças</p>
            </div>
          </Col>
          <Col sm={6}>
            <div className="text-center">
              <h3 className="text-success mb-1">
                {formatCurrency(data.averageBillingValue)}
              </h3>
              <p className="text-muted mb-0">Valor Médio</p>
            </div>
          </Col>
        </Row>

        <div className="mb-4">
          <h6 className="mb-3">Distribuição de Valores:</h6>

          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="text-success">
                <i className="feather icon-check-circle mr-1" />
                Pago
              </span>
              <span className="font-weight-bold">
                {formatCurrency(data.paidAmount)}
              </span>
            </div>
            <ProgressBar
              now={paidPercentage}
              variant="success"
              style={{ height: '8px' }}
            />
            <small className="text-muted">
              {paidPercentage.toFixed(1)}% do total
            </small>
          </div>

          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="text-warning">
                <i className="feather icon-clock mr-1" />
                Pendente
              </span>
              <span className="font-weight-bold">
                {formatCurrency(data.pendingAmount)}
              </span>
            </div>
            <ProgressBar
              now={pendingPercentage}
              variant="warning"
              style={{ height: '8px' }}
            />
            <small className="text-muted">
              {pendingPercentage.toFixed(1)}% do total
            </small>
          </div>

          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="text-danger">
                <i className="feather icon-alert-circle mr-1" />
                Vencido
              </span>
              <span className="font-weight-bold">
                {formatCurrency(data.overdueAmount)}
              </span>
            </div>
            <ProgressBar
              now={overduePercentage}
              variant="danger"
              style={{ height: '8px' }}
            />
            <small className="text-muted">
              {overduePercentage.toFixed(1)}% do total
            </small>
          </div>
        </div>

        <div className="mb-4">
          <div className="bg-light p-3 rounded">
            <Row>
              <Col sm={6}>
                <div className="text-center">
                  <h4 className="text-info mb-1">
                    {data.averagePaymentTime.toFixed(1)}
                  </h4>
                  <small className="text-muted">
                    Dias médios para pagamento
                  </small>
                </div>
              </Col>
              <Col sm={6}>
                <div className="text-center">
                  <h4 className="text-primary mb-1">
                    {formatNumber(data.blockchainRegisteredBillings)}
                  </h4>
                  <small className="text-muted">
                    Registradas na blockchain
                  </small>
                </div>
              </Col>
            </Row>
          </div>
        </div>

        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-muted">Registros Blockchain</span>
            <span className="font-weight-bold">
              {data.blockchainRegisteredBillings}/{data.totalBillings}
            </span>
          </div>
          <ProgressBar
            now={blockchainPercentage}
            variant="primary"
            style={{ height: '6px' }}
          />
          <small className="text-muted">
            {blockchainPercentage.toFixed(1)}% das cobranças registradas
          </small>
        </div>

        <div className="mt-4 pt-3 border-top">
          <small className="text-muted">
            <i className="feather icon-shield mr-1" />
            Sistema de cobrança totalmente integrado com blockchain para máxima
            transparência
          </small>
        </div>
      </Card.Body>
    </Card>
  )
}
